/**
 * Date/Time Utility Functions
 * Converts UTC timestamps to IST (Indian Standard Time) for display
 */

/**
 * Convert UTC timestamp to IST formatted string
 * @param {string|Date} utcTimestamp - UTC timestamp string or Date object
 * @param {object} options - Formatting options
 * @param {boolean} options.includeTime - Include time in output (default: true)
 * @param {boolean} options.includeSeconds - Include seconds (default: false)
 * @param {boolean} options.shortFormat - Use short date format (default: false)
 * @returns {string} Formatted IST date/time string
 */
export const toIST = (utcTimestamp, options = {}) => {
  if (!utcTimestamp) return '-';
  
  const { includeTime = true, includeSeconds = false, shortFormat = false } = options;
  
  try {
    let dateStr = utcTimestamp;
    
    // Handle Date objects
    if (utcTimestamp instanceof Date) {
      dateStr = utcTimestamp.toISOString();
    }
    
    // Normalize: ensure proper UTC format
    if (typeof dateStr === 'string') {
      if (dateStr.endsWith('+00:00')) {
        dateStr = dateStr.slice(0, -6) + 'Z';
      } else if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
        dateStr = dateStr + 'Z';
      }
    }
    
    const date = new Date(dateStr);
    
    if (isNaN(date.getTime())) return '-';
    
    if (shortFormat) {
      // Short format: "24 Feb 2026"
      return date.toLocaleDateString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
    
    if (!includeTime) {
      // Date only: "24 Feb 2026"
      return date.toLocaleDateString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
    
    // Full format: "24 Feb 2026, 11:30 AM IST"
    const day = date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit' });
    const month = date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short' });
    const year = date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', year: 'numeric' });
    
    const timeOptions = {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    
    if (includeSeconds) {
      timeOptions.second = '2-digit';
    }
    
    const time = date.toLocaleTimeString('en-IN', timeOptions);
    
    return `${day} ${month} ${year}, ${time} IST`;
  } catch (error) {
    console.error('Date conversion error:', error);
    return '-';
  }
};

/**
 * Convert UTC timestamp to user's local timezone
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @param {object} options - Formatting options
 * @returns {string} Formatted local date/time string
 */
export const toLocalTime = (utcTimestamp, options = {}) => {
  if (!utcTimestamp) return '-';
  
  const { includeTime = true, includeSeconds = false } = options;
  
  try {
    let dateStr = utcTimestamp;
    
    if (utcTimestamp instanceof Date) {
      dateStr = utcTimestamp.toISOString();
    }
    
    if (typeof dateStr === 'string') {
      if (dateStr.endsWith('+00:00')) {
        dateStr = dateStr.slice(0, -6) + 'Z';
      } else if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
        dateStr = dateStr + 'Z';
      }
    }
    
    const date = new Date(dateStr);
    
    if (isNaN(date.getTime())) return '-';
    
    const dateOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    };
    
    if (includeTime) {
      dateOptions.hour = '2-digit';
      dateOptions.minute = '2-digit';
      dateOptions.hour12 = true;
      if (includeSeconds) {
        dateOptions.second = '2-digit';
      }
    }
    
    return date.toLocaleString('en-IN', dateOptions);
  } catch (error) {
    console.error('Date conversion error:', error);
    return '-';
  }
};

/**
 * Get relative time string (e.g., "2 hours ago", "3 days ago")
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @returns {string} Relative time string
 */
export const getRelativeTime = (utcTimestamp) => {
  if (!utcTimestamp) return '-';
  
  try {
    const date = new Date(utcTimestamp);
    if (isNaN(date.getTime())) return '-';
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    // For older dates, show the actual date
    return toIST(utcTimestamp, { includeTime: false });
  } catch (error) {
    return '-';
  }
};

/**
 * Format date for display in forms (YYYY-MM-DD)
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    
    return d.toISOString().split('T')[0];
  } catch (error) {
    return '';
  }
};

/**
 * Check if a date is in the past
 * @param {string|Date} date - Date to check
 * @returns {boolean}
 */
export const isPastDate = (date) => {
  if (!date) return false;
  
  try {
    const d = new Date(date);
    return d < new Date();
  } catch (error) {
    return false;
  }
};

/**
 * Get days remaining until a date
 * @param {string|Date} date - Target date
 * @returns {number} Days remaining (negative if past)
 */
export const getDaysRemaining = (date) => {
  if (!date) return null;
  
  try {
    const target = new Date(date);
    const now = new Date();
    const diffMs = target - now;
    return Math.ceil(diffMs / 86400000);
  } catch (error) {
    return null;
  }
};
